import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {ArrowRight, Lock, LockKeyhole, Mail, ShieldCheck, Sparkles} from "lucide-react";
import {adminLogin, adminRecoverPassword} from "@corporatesclub/shared";
import {useAdminAuth} from "@/contexts/AdminAuthContext";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";

export function AdminLoginForm() {
  const navigate = useNavigate();
  const {refresh, applySession} = useAdminAuth();
  const [mail, setMail] = useState("");
  const [parola, setParola] = useState("");
  const [recover, setRecover] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const session = await adminLogin(mail, parola);
      if(!session.authenticated) { setErr("Authentication failed"); return; }
      applySession(session);
      const verified = await refresh();
      if(!verified.authenticated) { setErr("Session could not be validated. Please try again."); return; }
      navigate("/vadmin", {replace: true});
    } catch(ex) {
      setErr(ex instanceof Error ? ex.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRecover(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminRecoverPassword(mail);
      setMsg("A new password was sent by email.");
    } catch(ex) {
      setErr(ex instanceof Error ? ex.message : "Email not found");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-theme admin-login-page">
      <aside className="login-visual" aria-label="CorporatesClub admin">
        <div className="login-visual__fx" aria-hidden="true">
          <span className="login-visual__glow login-visual__glow--tr" />
          <span className="login-visual__glow login-visual__glow--bl" />
          <span className="login-visual__grid" />
          <span className="login-visual__pattern" />
          <span className="login-visual__mark">
            <svg viewBox="0 0 420 420" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M210 42c-92.8 0-168 75.2-168 168s75.2 168 168 168c54.4 0 102.4-25.9 133.3-65.8"
                stroke="url(#adminLoginMarkStroke)"
                strokeWidth="28"
                strokeLinecap="round"
                opacity="0.9"
              />
              <path
                d="M210 78c-72.9 0-132 59.1-132 132s59.1 132 132 132c42.7 0 80.4-20.3 104.7-51.7"
                stroke="rgba(255,75,11,0.22)"
                strokeWidth="14"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="adminLoginMarkStroke" x1="42" y1="42" x2="360" y2="360" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#ff7a3d" stopOpacity="0.55" />
                  <stop offset="0.55" stopColor="#ff4b0b" stopOpacity="0.35" />
                  <stop offset="1" stopColor="#ff4b0b" stopOpacity="0.08" />
                </linearGradient>
              </defs>
            </svg>
          </span>
        </div>

        <div className="login-visual__inner">
          <img
            src="/images/logo.png"
            alt="CorporatesClub"
            className="login-visual__logo"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/logo-dark.png"; }}
          />
          <p className="login-visual__tag">Admin panel</p>
          <h1 className="login-visual__title">
            Manage your B2B store with <span className="accent">clarity.</span>
          </h1>
          <p className="login-visual__desc">
            Orders, catalog, clients, and content — all in one place, aligned with the CorporatesClub experience.
          </p>
          <ul className="login-visual__features">
            <li className="login-visual__feature">
              <span className="login-visual__featureIcon"><ShieldCheck aria-hidden="true" /></span>
              <span className="login-visual__featureText">
                <strong>Secure access</strong>
                <span>for administrators</span>
              </span>
            </li>
            <li className="login-visual__feature">
              <span className="login-visual__featureIcon"><Sparkles aria-hidden="true" /></span>
              <span className="login-visual__featureText">
                <strong>Modern, fast</strong>
                <span>and clear interface</span>
              </span>
            </li>
            <li className="login-visual__feature">
              <span className="login-visual__featureIcon"><LockKeyhole aria-hidden="true" /></span>
              <span className="login-visual__featureText">
                <strong>Session-aware</strong>
                <span>admin controls</span>
              </span>
            </li>
          </ul>
        </div>
      </aside>

      <section className="login-content">
        <div className="login-content__fx" aria-hidden="true" />
        <div className="login-content__inner">
          <div className="login-card">
            <div className="login-card__icon" aria-hidden="true">
              <LockKeyhole />
            </div>
            <h2 className="login-card__title">{recover ? "Password recovery" : "Welcome back"}</h2>
            <p className="login-card__subtitle">
              {recover
                ? "Enter your email address and your password will be reset."
                : "Sign in to your administrator account"}
            </p>

            {err ? (
              <div className="login-card__alert login-card__alert--error" role="alert">{err}</div>
            ) : null}
            {msg ? (
              <div className="login-card__alert login-card__alert--success" role="status">{msg}</div>
            ) : null}

            {recover ? (
              <form className="login-form" onSubmit={onRecover}>
                <div className="login-field">
                  <label className="login-field__label" htmlFor="admin-login-mail">E-mail</label>
                  <div className="login-field__control">
                    <Mail className="login-field__icon" aria-hidden="true" />
                    <Input
                      id="admin-login-mail"
                      type="email"
                      name="mail"
                      value={mail}
                      onChange={(e) => setMail(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={busy}
                      placeholder="you@example.com"
                      className="login-field__input"
                    />
                  </div>
                </div>
                <div className="login-form__actions login-form__actions--split">
                  <Button
                    type="button"
                    variant="outline"
                    className="login-btn login-btn--ghost"
                    onClick={() => { setRecover(false); setMsg(""); setErr(""); }}
                    disabled={busy}
                  >
                    Back
                  </Button>
                  <Button type="submit" className="login-btn login-btn--primary" disabled={busy}>
                    {busy ? "Sending..." : "Send"}
                  </Button>
                </div>
              </form>
            ) : (
              <form className="login-form" onSubmit={onLogin}>
                <div className="login-field">
                  <label className="login-field__label" htmlFor="admin-login-mail">E-mail</label>
                  <div className="login-field__control">
                    <Mail className="login-field__icon" aria-hidden="true" />
                    <Input
                      id="admin-login-mail"
                      type="email"
                      name="mail"
                      value={mail}
                      onChange={(e) => setMail(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={busy}
                      placeholder="you@example.com"
                      className="login-field__input"
                    />
                  </div>
                </div>
                <div className="login-field">
                  <label className="login-field__label" htmlFor="admin-login-parola">Password</label>
                  <div className="login-field__control">
                    <Lock className="login-field__icon" aria-hidden="true" />
                    <Input
                      id="admin-login-parola"
                      type="password"
                      name="parola"
                      value={parola}
                      onChange={(e) => setParola(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={busy}
                      placeholder="Enter your password"
                      className="login-field__input"
                    />
                  </div>
                </div>
                <div className="login-form__meta">
                  <button
                    type="button"
                    className="login-forgot"
                    onClick={() => { setRecover(true); setErr(""); setMsg(""); }}
                    disabled={busy}
                  >
                    Forgot password?
                  </button>
                </div>
                <Button type="submit" className="login-btn login-btn--primary login-btn--submit" disabled={busy}>
                  <span>{busy ? "Signing in..." : "Sign in"}</span>
                  {!busy ? <ArrowRight className="login-btn__arrow" aria-hidden="true" /> : null}
                </Button>
              </form>
            )}
          </div>

          <p className="login-secure">
            <ShieldCheck aria-hidden="true" />
            <span>Your connection is secure and encrypted</span>
          </p>
        </div>
      </section>
    </div>
  );
}
